// Thin wrapper around @capacitor/push-notifications. Everything is a no-op on
// web (IS_CAPACITOR === false). The plugin is imported statically — a lazy
// import() can silently fail to load its chunk inside a WKWebView, which is
// hard to diagnose, so we bundle it directly and just guard the calls.

import { IS_CAPACITOR } from './config';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerPushToken } from './authClient';

let listenersBound = false;
let lastError = null;
// Tracks the outcome of the last device-registration attempt for diagnostics.
let registration = { state: 'idle', detail: '' }; // idle | pending | ok | error
let openArtistHandler = (slug, connected) => {
  // Default deep-link: full navigation re-runs App.js routing -> ArtistLandingPage.
  const query = connected ? `?connected=${encodeURIComponent(connected)}` : '';
  window.location.assign(`/artist/${slug}${query}`);
};

function noteError(e) {
  lastError = (e && (e.message || String(e))) || 'unknown error';
  console.error('[push]', lastError);
}

// Surfaced in the settings UI when push can't initialize, for diagnosis.
export function getLastPushError() {
  return lastError;
}

// { state, detail } describing the last APNs token registration attempt.
export function getPushDiagnostics() {
  return { ...registration, lastError };
}

function deviceTzOffset() {
  try {
    return new Date().getTimezoneOffset();
  } catch {
    return null;
  }
}

export function setOpenArtistHandler(fn) {
  if (typeof fn === 'function') openArtistHandler = fn;
}

// Bind listeners once (token registration + notification taps).
export async function initPush({ onOpenArtist } = {}) {
  if (!IS_CAPACITOR) return;
  if (onOpenArtist) setOpenArtistHandler(onOpenArtist);
  if (listenersBound) return;
  listenersBound = true;

  try {
    PushNotifications.addListener('registration', async (token) => {
      try {
        const res = await registerPushToken(token.value, 'ios', deviceTzOffset());
        if (res && res.ok === false) {
          registration = { state: 'error', detail: `server returned ${res.status} saving token` };
        } else {
          registration = { state: 'ok', detail: `token ${String(token.value).slice(0, 8)}…` };
        }
      } catch (e) {
        registration = { state: 'error', detail: `saving token failed: ${e?.message || e}` };
      }
    });
    PushNotifications.addListener('registrationError', (err) => {
      const msg = err?.error || err?.message || JSON.stringify(err);
      registration = { state: 'error', detail: `APNs registration failed: ${msg}` };
      noteError(err);
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data || {};
      if (data.artistSlug) openArtistHandler(data.artistSlug, data.connected);
    });
  } catch (e) {
    listenersBound = false;
    noteError(e);
  }
}

// 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | 'unavailable'
export async function getPermissionStatus() {
  if (!IS_CAPACITOR) return 'unavailable';
  try {
    const res = await PushNotifications.checkPermissions();
    return res.receive;
  } catch (e) {
    noteError(e);
    return 'unavailable';
  }
}

// Request OS permission (if not yet decided) and register for a device token.
// Returns the resulting permission status, or 'unavailable' if push can't run.
export async function requestAndRegister() {
  if (!IS_CAPACITOR) return 'unavailable';
  try {
    await initPush();
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') {
      registration = { state: 'pending', detail: '' };
      await PushNotifications.register();
    }
    return perm.receive;
  } catch (e) {
    noteError(e);
    return 'unavailable';
  }
}
